/*
 * The basket puts a suit back together.
 *
 * A suit sold as three pieces reaches the cart as three separate lines - a
 * blazer, a waistcoat and a pair of trousers with nothing on screen to say
 * they are one thing. The customer chose "the Belmont" and the basket shows
 * them three garments and three prices, which reads as a mistake.
 *
 * Grouping is done here rather than in Liquid because the three lines are not
 * necessarily next to each other: quantity changes and re-adds move them, and
 * a group has to survive that. The theme rebuilds this list in place on every
 * cart change, so an observer regroups whatever it leaves behind.
 *
 * Nothing is removed or reordered destructively - the rows are the theme's own
 * and keep their merge keys, so the cart's own machinery still finds them.
 */
(function () {
  var SET = '[data-tc-set]';
  var GONE = '.merge-remove-wrapper';

  /*
   * A row on its way out is not part of the set any more.
   *
   * Removing a line does not delete its row: the theme parks it in a
   * .merge-remove-wrapper, collapses that, and deletes the wrapper when the
   * animation ends. Regrouping around a row in that state put the group box
   * inside the wrapper - and when the wrapper went, the whole suit went with
   * it, emptying a basket that still had two garments in it.
   */
  function leaving(row) {
    return !!(row.closest(GONE) || row.classList.contains('merge-remove-item'));
  }

  /**
   * What the rows are wearing — learned, not assumed.
   *
   * The heading used to be '£' plus the number, which is right until Shopify
   * Markets prices the basket in something else: a Swiss cart read CHF on
   * every line and £ on the set heading directly above them. Shopify's own
   * formatMoney is no help, since it carries the shop's format rather than the
   * market's and would put the £ straight back.
   *
   * So it copies the row it just read the price off: prefix and suffix both,
   * because not every currency goes in front of the number.
   */
  var SHAPE = null;

  function money(pence) {
    var s = (pence / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d)\.)/g, ',');
    if (SHAPE) return SHAPE.prefix + s + SHAPE.suffix;
    return '£' + s;
  }

  /**
   * The visible price, without the words only a screen reader was meant to
   * hear.
   *
   * Symmetry labels its prices with a hidden "Regular price" span inside the
   * same element, and textContent does not know the difference — so reading
   * the shape off the raw text put "Regular price £215.00" in the set heading.
   * The number always parsed correctly; it was the prefix that came back
   * carrying a sentence.
   */
  function priceText(el) {
    if (!el) return '';
    var clone = el.cloneNode(true);
    var hidden = clone.querySelectorAll('.visually-hidden, .visuallyhidden, .sr-only');
    for (var i = 0; i < hidden.length; i++) hidden[i].parentNode.removeChild(hidden[i]);
    return String(clone.textContent).replace(/\s+/g, ' ').trim();
  }

  /** The line total of a row, read off the theme's own markup. */
  function priceOf(row) {
    var el = row.querySelector('.theme-money, .cart-item__price, [class*="price"]');
    var text = priceText(el);
    var n = parseFloat(text.replace(/[^0-9.]/g, ''));
    if (!isFinite(n)) return 0;

    if (!SHAPE) {
      var run = text.match(/\d[\d.,\u00a0 ]*\d|\d/);
      if (run) {
        SHAPE = {
          prefix: text.slice(0, run.index),
          suffix: text.slice(run.index + run[0].length),
        };
      }
    }
    return Math.round(n * 100);
  }

  function group(list) {
    if (!list) return;

    /* Anything we built last time comes apart first: the rows inside it may
       have been replaced, removed, or joined by a fourth. Rebuilding from the
       rows themselves is the only version that cannot drift. */
    [].slice.call(list.querySelectorAll('.tc-cartset')).forEach(function (old) {
      /* Rows come out beside the wrapper, not inside it, in case an older
         pass left a box somewhere the theme is about to delete. */
      var anchor = old.closest(GONE) || old;
      var parent = anchor.parentNode;
      if (!parent) return;
      while (old.firstChild) {
        var node = old.firstChild;
        if (node.classList && node.classList.contains('tc-cartset__head')) { old.removeChild(node); continue; }
        parent.insertBefore(node, anchor);
      }
      if (old.parentNode) old.parentNode.removeChild(old);
    });

    var rows = [].slice.call(list.querySelectorAll('[data-merge-list-item]'));

    /* Every piece in the basket, with the set it says it belongs to. */
    var pieces = [];
    rows.forEach(function (row) {
      if (leaving(row)) return;
      var item = row.querySelector(SET);
      if (!item) return;
      var parts = (item.getAttribute('data-tc-parts') || '').split(',').filter(Boolean);
      pieces.push({
        row: row,
        handle: item.getAttribute('data-tc-handle') || '',
        key: item.getAttribute('data-tc-set'),
        name: item.getAttribute('data-tc-set-name') || 'Suit',
        parts: parts,
      });
    });

    var seen = [];
    var byKey = {};
    var claimed = [];

    /*
     * The suits that are actually here, before anything else.
     *
     * A piece can belong to more than one suit - the Viceroy blue blazer is
     * sold in its own Prince of Wales set and again beside a Harris brown
     * waistcoat - and suit_set.suit names only one of them. Grouping by that
     * pointer split a complete suit in two: blazer and trousers under the
     * mix-and-match parent they point at, the blue waistcoat left below as a
     * loose garment, and a heading that read "2 pieces" over three.
     *
     * So a piece is asked what its set consists of rather than who its parent
     * is. Any one of them names all three, so a set is here when each handle it
     * names is on a row - and the row that named it is the one whose parent
     * gives the group its title. A piece is spent once, so the same blazer
     * cannot appear in two suits.
     */
    pieces.forEach(function (piece) {
      if (!piece.parts.length || byKey[piece.key]) return;
      var picked = [];
      var ok = piece.parts.every(function (handle) {
        for (var i = 0; i < pieces.length; i++) {
          var other = pieces[i];
          if (other.handle !== handle) continue;
          if (claimed.indexOf(other) > -1 || picked.indexOf(other) > -1) continue;
          picked.push(other);
          return true;
        }
        return false;
      });
      if (!ok || picked.length < 2) return;
      picked.forEach(function (p) { claimed.push(p); });
      /* Back into the order the basket has them in. The group is inserted where
         its first row sits, so "first" has to mean the highest one on the page
         and not whichever piece the set happened to name first. */
      picked.sort(function (a, b) { return pieces.indexOf(a) - pieces.indexOf(b); });
      byKey[piece.key] = { rows: picked.map(function (p) { return p.row; }), name: piece.name };
      seen.push(piece.key);
    });

    /*
     * Then whatever is left, by the parent it points at.
     *
     * Half a suit is still worth showing as half a suit: a man who has put the
     * blazer and the trousers in his basket and not the waistcoat should see
     * them together, under the name of the suit they came from.
     */
    pieces.forEach(function (piece) {
      if (claimed.indexOf(piece) > -1) return;
      var key = piece.key;
      if (!byKey[key]) { byKey[key] = { rows: [], name: piece.name }; seen.push(key); }
      byKey[key].rows.push(piece.row);
    });

    seen.forEach(function (key) {
      var set = byKey[key];
      /* One piece on its own is not a set. A man who removed the waistcoat has
         a blazer and trousers, and calling that "the Belmont suit" over one
         price would be telling him he is buying something he is not. */
      if (set.rows.length < 2) return;

      var box = document.createElement('div');
      box.className = 'tc-cartset';
      box.setAttribute('data-tc-cartset', key);

      var total = set.rows.reduce(function (a, r) { return a + priceOf(r); }, 0);
      var head = document.createElement('div');
      head.className = 'tc-cartset__head';
      head.innerHTML =
        '<span class="tc-cartset__kind">' + (set.rows.length === 3 ? '3 piece suit' : set.rows.length + ' pieces') + '</span>' +
        '<span class="tc-cartset__name"></span>' +
        '<span class="tc-cartset__total">' + money(total) + '</span>';
      head.querySelector('.tc-cartset__name').textContent = set.name;

      /* Every kept row is a child of the list by now; anything else means the
         theme is mid-edit, and the next pass can group it. */
      var first = set.rows[0];
      if (first.parentNode !== list) return;
      list.insertBefore(box, first);
      box.appendChild(head);
      set.rows.forEach(function (r) { box.appendChild(r); });
    });
  }

  function watch() {
    var list = document.querySelector('[data-merge-list="cart-items"]');
    if (!list) return false;
    group(list);
    new MutationObserver(function () {
      /* Regrouping edits the list, which would call this again. The flag is
         cheaper than disconnecting and reconnecting around every pass. */
      if (watch.busy) return;
      watch.busy = true;
      try { group(list); } finally { setTimeout(function () { watch.busy = false; }, 0); }
    }).observe(list, { childList: true, subtree: true });
    return true;
  }

  if (!watch()) {
    /* The drawer is rendered with the page but the list can arrive with the
       first cart change on a page that started empty. */
    var tries = 0;
    var timer = setInterval(function () {
      if (watch() || ++tries > 40) clearInterval(timer);
    }, 250);
  }
})();
